import { useEffect, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { getAnnotationToolAdminProjectsPath } from '@verantos-dev/core-annotation-tool';
import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import {
  useGetAccuracyBranchesQuery,
  useGetAccuracyModelsQuery,
  useGetAccuracyPromptsQuery,
  useGetBatchesForAccuracyQuery,
  useTriggerAccuracyMutation,
} from './api/accuracyApiSlice';
import { PICK_AI_PROMPT_BRANCH_FF } from './constants';
import { formatBatchTypeLabel } from './emitters/formatBatchTypeLabel';
import { formatRunSummary } from './emitters/formatRunSummary';
import { useCanGenerateAccuracy } from './hooks/useCanGenerateAccuracy';
import {
  generateAccuracyReducer,
  initialGenerateAccuracyState,
} from './state/generateAccuracyReducer';
import type { AccuracyBatchOption } from './types/response';
import { composeJobRunDescription } from './utils/composeJobRunDescription';
import { getErrorMessage } from './utils/extractErrorMessage';
import { getGenerateAccuracyValidation } from './utils/generateAccuracyValidation';
import { useGetProjectDetailsQuery } from '../admin-projects/api/adminProjectsApiSlice';

export const useGenerateAccuracyScreen = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const canAccess = useCanGenerateAccuracy();

  const [state, dispatch] = useReducer(generateAccuracyReducer, initialGenerateAccuracyState);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: projectDetailsData, isLoading: isProjectLoading } = useGetProjectDetailsQuery(
    { projectId },
    { skip: !projectId }
  );

  const {
    data: batchesData,
    isLoading: isBatchesLoading,
    isError: isBatchesError,
    error: batchesError,
  } = useGetBatchesForAccuracyQuery({ projectId }, { skip: !projectId });

  const {
    data: modelsData,
    isLoading: isModelsLoading,
    isError: isModelsError,
    error: modelsError,
  } = useGetAccuracyModelsQuery();

  // TODO: cleanup with cp_pick_ai_prompt_branch ff
  const isBranchPickerOn = Boolean(useFeatureFlagSplit(PICK_AI_PROMPT_BRANCH_FF));

  const {
    data: branchesData,
    isFetching: isBranchesLoading,
    isError: isBranchesError,
    error: branchesError,
  } = useGetAccuracyBranchesQuery(undefined, { skip: !isBranchPickerOn });

  // Prompts are keyed by the project's org IDN in ai-prompts — fetched on load,
  // not gated on model/branch selection (the dropdowns themselves stay disabled
  // until a model is picked, matching the existing step gating). With the branch
  // picker flag on the query carries `branch` and refetches when it changes;
  // flag off sends no branch param so the request is byte-identical to before.
  // The default branch comes from the API (AI_PROMPTS_DEFAULT_BRANCH on the
  // backend) — state.branch stays null until the user picks one, so the
  // resolved selection falls back to whatever the backend is configured with.
  const defaultBranch = branchesData?.data?.defaultBranch;
  const branches = useMemo(() => branchesData?.data?.branches ?? [], [branchesData]);
  const selectedBranch = state.branch ?? defaultBranch;

  const {
    data: promptsData,
    isFetching: isPromptsLoading,
    isError: isPromptsError,
    error: promptsError,
  } = useGetAccuracyPromptsQuery(
    { projectId, branch: isBranchPickerOn ? selectedBranch : undefined },
    { skip: !projectId }
  );

  const [triggerAccuracy, { isLoading: isSubmitting }] = useTriggerAccuracyMutation();

  const batches = useMemo(() => batchesData?.data ?? [], [batchesData]);
  const models = useMemo(() => modelsData?.data ?? [], [modelsData]);
  const prompts = useMemo(() => promptsData?.data ?? [], [promptsData]);

  const selectedBatch = useMemo(
    () => batches.find((b) => b.batchId === state.batchId),
    [batches, state.batchId]
  );
  const selectedModel = useMemo(
    () => models.find((m) => m.modelId === state.modelId),
    [models, state.modelId]
  );
  const selectedPrompt = useMemo(
    () => prompts.find((p) => p.promptPath === state.promptPath),
    [prompts, state.promptPath]
  );

  // The chosen prompt can fall out of the list when the project/IDN prompt set
  // changes — the pure reducer can't see server data, so the hook dispatches the prune.
  useEffect(() => {
    if (
      state.promptPath &&
      promptsData &&
      !prompts.some((p) => p.promptPath === state.promptPath)
    ) {
      dispatch({ type: 'pruneInvalidPrompt' });
    }
  }, [promptsData, prompts, state.promptPath]);

  const batchTypeLabel = state.batchType
    ? formatBatchTypeLabel(state.batchType, formatMessage)
    : null;

  // The description names the branch only when it isn't the default — a run
  // against the default branch reads the same as before the picker existed.
  const branchLabel =
    isBranchPickerOn && selectedBranch && selectedBranch !== defaultBranch ? selectedBranch : null;

  const composedDescription = useMemo(
    () =>
      composeJobRunDescription(
        {
          batchTypeLabel,
          modelLabel: selectedModel?.displayName ?? null,
          promptLabel: selectedPrompt?.promptName ?? null,
          branchLabel,
        },
        formatMessage
      ),
    [batchTypeLabel, selectedModel, selectedPrompt, branchLabel, formatMessage]
  );

  // Auto-write the composed description until the user edits it by hand.
  useEffect(() => {
    if (
      !state.isDescriptionManuallyEdited &&
      composedDescription &&
      composedDescription !== state.description
    ) {
      dispatch({ type: 'applyComposedDescription', text: composedDescription });
    }
  }, [composedDescription, state.isDescriptionManuallyEdited, state.description]);

  // A hand-typed value that happens to match the composed text is not an override.
  useEffect(() => {
    if (
      state.isDescriptionManuallyEdited &&
      composedDescription &&
      state.description === composedDescription
    ) {
      dispatch({ type: 'resetDescription' });
    }
  }, [state.description, state.isDescriptionManuallyEdited, composedDescription]);

  const validation = useMemo(
    () => getGenerateAccuracyValidation(state, selectedBatch),
    [state, selectedBatch]
  );

  const projectDetails = projectDetailsData?.data;
  const projectLabel = projectDetails?.basics.projectName ?? '';

  const runSummary = useMemo(
    () =>
      formatRunSummary({
        projectLabel,
        batchTypeLabel,
        modelLabel: selectedModel?.displayName ?? null,
        promptName: selectedPrompt?.promptName ?? null,
      }),
    [projectLabel, batchTypeLabel, selectedModel, selectedPrompt]
  );

  const handleChangeProject = () => navigate(getAnnotationToolAdminProjectsPath());

  const handleSelectBatch = (batch: AccuracyBatchOption) =>
    dispatch({ type: 'selectBatch', batchId: batch.batchId, batchType: batch.batchType });

  const handleConfirmTestBatch = (batch: AccuracyBatchOption) =>
    dispatch({
      type: 'confirmTestBatchSelection',
      batchId: batch.batchId,
      batchType: batch.batchType,
    });

  const handleSwitchToValidation = (batch: AccuracyBatchOption) =>
    dispatch({
      type: 'switchToValidationBatch',
      batchId: batch.batchId,
      batchType: batch.batchType,
    });

  const handleSelectModel = (modelId: string) => dispatch({ type: 'selectModel', modelId });

  const handleSelectPrompt = (promptPath: string) => dispatch({ type: 'selectPrompt', promptPath });

  const handleSelectBranch = (branch: string) => dispatch({ type: 'selectBranch', branch });

  const handleEditDescription = (text: string) => dispatch({ type: 'editDescription', text });

  const handleResetDescription = () => dispatch({ type: 'resetDescription' });

  const handleGenerate = async () => {
    if (!validation.canGenerate || !projectId || !selectedBatch || !selectedModel) {
      return;
    }
    if (!state.promptPath) {
      return;
    }
    try {
      const result = await triggerAccuracy({
        project_id: projectId,
        batch_id: selectedBatch.batchId,
        batch_type: selectedBatch.batchType,
        bedrock_model_name: selectedModel.modelId,
        prompt_name: state.promptPath,
        // Only sent when the branch picker flag is on — flag-off payloads stay
        // identical to before; the backend treats absent as its default branch.
        ...(isBranchPickerOn && selectedBranch ? { prompt_branch: selectedBranch } : {}),
        description: state.description,
      }).unwrap();

      navigate(getAnnotationToolAdminProjectsPath(), {
        state: {
          submittedJob: {
            projectName: projectLabel,
            jobReference: result.data.jobReference,
          },
        },
      });
    } catch (error) {
      setSubmitError(
        getErrorMessage(error) ??
          formatMessage({ id: 'adminProjects.generateAccuracy.submitError' })
      );
    }
  };

  return {
    canAccess,
    projectId,
    projectName: projectLabel,
    conditionAreaName: projectDetails?.basics.conditionAreaName ?? '',
    healthSystemName: projectDetails?.basics.healthSystemName ?? '',
    isProjectLoading,

    batches,
    isBatchesLoading,
    isBatchesError,
    batchesError,

    models,
    isModelsLoading,
    isModelsError,
    modelsError,

    prompts,
    isPromptsLoading,
    isPromptsError,
    promptsError,

    isBranchPickerOn,
    branches,
    selectedBranch,
    isBranchesLoading,
    isBranchesError,
    branchesError,

    state,
    validation,
    runSummary,
    isSubmitting,
    submitError,

    handleChangeProject,
    handleSelectBatch,
    handleConfirmTestBatch,
    handleSwitchToValidation,
    handleSelectModel,
    handleSelectBranch,
    handleSelectPrompt,
    handleEditDescription,
    handleResetDescription,
    handleGenerate,
  };
};

export type UseGenerateAccuracyScreenResult = ReturnType<typeof useGenerateAccuracyScreen>;
